import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { auth_api_instance } from "src/api/snugtotal/auth";

// Multi-factor authentication (MFA) calls on the auth API. The bearer token for the
// session endpoints is attached by the auth_api_instance request interceptor.

export type MfaKind = "totp" | "email" | "passkey";

// Returned instead of tokens by password login (and the email-verify link) when the user has MFA set up.
// "passkey" is only listed when they have a passkey for this site; an empty list means their only
// method is a passkey made on another site, so a recovery code is the way in.
export type MfaChallenge = {
  mfa_required: true;
  mfa_token: string;
  methods: MfaKind[];
  email_hint?: string | null;
  email_sent?: boolean;
};

export type MfaDevice = {
  id: number | string;
  kind: MfaKind;
  created_at: string;
  last_used_at: string | null;
  // Passkeys only: what they called it, and the site it works on.
  name?: string | null;
  domain?: string | null;
};

export type MfaStatus = {
  enrolled: boolean;
  session_mfa: boolean;
  devices: MfaDevice[];
  recovery_codes_remaining: number;
  // How many devices skip the code because "Remember this device" was ticked.
  trusted_devices?: number;
};

export type MfaSetupResponse = {
  setup_token: string;
  kind: MfaKind;
  secret?: string;
  otpauth_uri?: string;
  email_hint?: string;
};

export type MfaTokens = {
  access: string;
  refresh: string;
  recovery_codes_remaining?: number;
  // Only when they ticked "Remember this device"; saved by the verify calls below.
  device_token?: string;
};

export type MfaSetupConfirmResponse = {
  access: string;
  refresh: string;
  recovery_codes: string[] | null;
};

export type PasskeySetupOptions = {
  setup_token: string;
  options: PublicKeyCredentialCreationOptionsJSON;
};

export type PasskeyLoginOptions = {
  options: PublicKeyCredentialRequestOptionsJSON;
};

export const isMfaChallenge = (data: any): data is MfaChallenge =>
  Boolean(data?.mfa_required && data?.mfa_token);

// Thrown by sign-in when the password was right but a code is still needed.
// Checked by shape (not instanceof) so it survives transpiling Error subclasses.
export type MfaRequiredError = Error & { mfaChallenge: MfaChallenge };

export const mfaRequiredError = (challenge: MfaChallenge): MfaRequiredError =>
  Object.assign(new Error("MFA code required"), { mfaChallenge: challenge });

export const getMfaChallenge = (err: any): MfaChallenge | null =>
  isMfaChallenge(err?.mfaChallenge) ? err.mfaChallenge : null;

// The API's own wording ({code, details}) when it gives one, otherwise a plain fallback.
export const mfaErrorMessage = (err: any): string => {
  const data = err?.response?.data;
  if (data?.code === "mfa_step_up") {
    return "For your security, please sign out and sign back in with your code, then try again.";
  }
  if (typeof data?.details === "string" && data.details) return data.details;
  if (!err?.response) return "We couldn't reach the server. Check your connection and try again.";
  return "Something went wrong. Please try again.";
};

export const mfaErrorCode = (err: any): string | null =>
  err?.response?.data?.code || null;

// Codes are typed by hand, so drop spaces; recovery codes keep their dash.
export const cleanMfaCode = (code: string) => code.replace(/\s+/g, "").toLowerCase();

// Setup confirm returns tokens that carry MFA; they replace the current session.
export const saveSessionTokens = (access: string, refresh: string) => {
  localStorage.setItem("s_at", access);
  localStorage.setItem("s_rt", refresh);
};

// "Remember this device": a token the server gives back after a code or passkey with
// remember_device ticked. Sent with the next password / email-link sign-in to skip the code.
// Kept on normal sign-out (that's the point); dropped when the server asks for a code anyway.
const DEVICE_TOKEN_KEY = "snug_mfa_device_token";

export const getMfaDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch (err) {
    return null;
  }
};

const saveMfaDeviceToken = (token: string | null | undefined) => {
  if (!token) return;
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch (err) {
    console.error(err);
  }
};

export const clearMfaDeviceToken = () => {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch (err) {
    console.error(err);
  }
};

// Passkeys (Face ID / Touch ID / Windows Hello). They only work on the site they were made on.

export const passkeysSupported = (): boolean => {
  try {
    return browserSupportsWebAuthn();
  } catch (err) {
    return false;
  }
};

// The person closed or cancelled the browser's passkey prompt (or it timed out): not an error to show.
export const isPasskeyCancel = (err: any): boolean =>
  err?.name === "NotAllowedError" || err?.name === "AbortError";

export const passkeyErrorMessage = (err: any): string => {
  if (err?.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED" || err?.name === "InvalidStateError") {
    return "This device already has a passkey for your account.";
  }
  if (err?.response) return mfaErrorMessage(err);
  return "Your device couldn't use a passkey. Please try again, or use a code instead.";
};

// A friendly starting name for a new passkey, based on the device they're using.
export const defaultPasskeyName = (): string => {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  // iPads report as a Mac but have a touch screen.
  if (/Macintosh/i.test(ua)) return navigator.maxTouchPoints > 1 ? "iPad" : "Mac";
  if (/Android/i.test(ua)) return "Android phone";
  if (/Windows/i.test(ua)) return "Windows PC";
  if (/CrOS/i.test(ua)) return "Chromebook";
  return "This device";
};

// Login (no session yet)

export const verifyMfaLogin = async (
  mfa_token: string,
  code: string,
  remember_device: boolean = false
): Promise<MfaTokens> => {
  const response = await auth_api_instance.post("mfa/login/verify/", {
    mfa_token,
    code: cleanMfaCode(code),
    remember_device,
  });
  saveMfaDeviceToken(response.data?.device_token);
  return response.data;
};

export const getPasskeyLoginOptions = async (mfa_token: string): Promise<PasskeyLoginOptions> => {
  const response = await auth_api_instance.post("mfa/login/passkey/options/", { mfa_token });
  return response.data;
};

export const verifyPasskeyLogin = async (
  mfa_token: string,
  credential: AuthenticationResponseJSON,
  remember_device: boolean = false
): Promise<MfaTokens> => {
  const response = await auth_api_instance.post("mfa/login/passkey/", {
    mfa_token,
    credential,
    remember_device,
  });
  saveMfaDeviceToken(response.data?.device_token);
  return response.data;
};

export const sendMfaLoginEmail = async (mfa_token: string): Promise<{ sent: boolean }> => {
  const response = await auth_api_instance.post("mfa/login/email/", { mfa_token });
  return response.data;
};

// Signed-in management

export const getMfaStatus = async (): Promise<MfaStatus> => {
  const response = await auth_api_instance.get("mfa/");
  return response.data;
};

export const startMfaSetup = async (kind: MfaKind): Promise<MfaSetupResponse> => {
  const response = await auth_api_instance.post("mfa/setup/", { kind });
  return response.data;
};

export const resendMfaSetupEmail = async (setup_token: string): Promise<{ sent: boolean }> => {
  const response = await auth_api_instance.post("mfa/setup/email/", { setup_token });
  return response.data;
};

export const confirmMfaSetup = async (
  setup_token: string,
  code: string
): Promise<MfaSetupConfirmResponse> => {
  const response = await auth_api_instance.post("mfa/setup/confirm/", {
    setup_token,
    code: cleanMfaCode(code),
  });
  return response.data;
};

export const startPasskeySetup = async (): Promise<PasskeySetupOptions> => {
  const response = await auth_api_instance.post("mfa/setup/passkey/options/");
  return response.data;
};

export const finishPasskeySetup = async (
  setup_token: string,
  credential: RegistrationResponseJSON,
  name: string
): Promise<MfaSetupConfirmResponse> => {
  const response = await auth_api_instance.post("mfa/setup/passkey/", {
    setup_token,
    credential,
    name,
  });
  return response.data;
};

export const removeMfaDevice = async (id: MfaDevice["id"]): Promise<MfaStatus> => {
  const response = await auth_api_instance.delete(`mfa/devices/${id}/`);
  return response.data;
};

export const regenerateRecoveryCodes = async (): Promise<{ recovery_codes: string[] }> => {
  const response = await auth_api_instance.post("mfa/recovery-codes/");
  return response.data;
};

// Forget every remembered device (this one included); returns the new status.
export const forgetTrustedDevices = async (): Promise<MfaStatus> => {
  const response = await auth_api_instance.delete("mfa/trusted-devices/");
  clearMfaDeviceToken();
  return response.data;
};
